/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Heading, Box} from '@chakra-ui/react'
import {Region, regionPropType} from '@salesforce/commerce-sdk-react/components'
import multiImageContainer from '../../../theme/components/project/multi-image-container'

/**
 * This layout component displays MultiImagesContainer component
 *
 * @param {componentProps} props
 * @param {regionType []} props.regions - The page designer regions for this component.
 * @returns {React.ReactElement} - MultiImagesContainer component.
 */
export const MultiImagesContainer = ({
    regions,
    title,
    titleTextStyle,
    titleColor,
    backgroundColor,
    titlePosition,
    paddingRightMobile,
    paddingRightTablet,
    paddingRightDesktop,
    showSliderInMobile,
    customClass
}) => {
    const desktopPadding = paddingRightDesktop + 'px'
    const tabletPadding = paddingRightTablet + 'px'
    const mobilePadding = paddingRightMobile + 'px'
    return (
        <Box sx={multiImageContainer}>
            <Box
                className="multi-image-container-main-block"
                px={[mobilePadding, tabletPadding, desktopPadding]}
                bg={backgroundColor?.value}
                maxWidth={{base: '100%', xl: '1920px'}}
                margin="16px auto"
            >
                {title && (
                    <Heading
                        fontSize="2xl"
                        mb={4}
                        color={titleColor?.value}
                        textAlign={titlePosition}
                        fontStyle={titleTextStyle}
                    >
                        {title}
                    </Heading>
                )}

                <Box className={showSliderInMobile ? 'mobile-slider' : ''}>
                    {regions.map((region) => (
                        <Region
                            key={region.id}
                            region={region}
                            className={`multi-image-container ${customClass}`}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

MultiImagesContainer.displayName = 'MultiImagesContainer'

MultiImagesContainer.propTypes = {
    // Internally Provided
    regions: PropTypes.arrayOf(regionPropType).isRequired,
    title: PropTypes.string,
    titleTextStyle: PropTypes.string,
    titleColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    titlePosition: PropTypes.string,
    paddingTopMobile: PropTypes.number,
    paddingLeftMobile: PropTypes.number,
    paddingBottomMobile: PropTypes.number,
    paddingRightMobile: PropTypes.number,
    paddingTopTablet: PropTypes.number,
    paddingLeftTablet: PropTypes.number,
    paddingBottomTablet: PropTypes.number,
    paddingRightTablet: PropTypes.number,
    paddingTopDesktop: PropTypes.number,
    paddingLeftDesktop: PropTypes.number,
    paddingBottomDesktop: PropTypes.number,
    paddingRightDesktop: PropTypes.number,
    showSliderInMobile: PropTypes.bool,
    customClass: PropTypes.string
}

export default MultiImagesContainer
